import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Badge } from '@plato/ui-legacy/components/list-views/badge';
import { CollapsibleText } from '@plato/ui-legacy/components/ui/collapsible-text';
import { InfoTag } from '@plato/ui-legacy/components/ui/info-tag';
import Text from '@plato/ui-legacy/components/ui/text';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { cn } from '@plato/ui/util/classes';
import { intlFormat } from 'date-fns';
import {
    CalendarXIcon,
    ClipboardCheckIcon,
    ClockIcon,
    PaperclipIcon,
    PhoneOffIcon,
    PinIcon,
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import type { CrmNoteDTO } from '../../../server/dto/crm-note-dto.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { EditNoteDialog } from '../../notes/components/edit-note-dialog.tsx';
import { getNoteTypeLabel } from '../../notes/utils/get-note-type-label.ts';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';
import { TimelineList } from '../../shared/components/timeline-list/timeline-list.tsx';

export const NoteTimelineItem = React.forwardRef<HTMLDivElement, { note: CrmNoteDTO }>(({ note }, ref) => {
    const { t } = useTranslation();
    const locale = useResolvedLocale();

    const sendEvent = useAnalytics();

    const { ref: inViewRef } = useInView({
        threshold: 0,
        onChange: (inView) => {
            if (inView) {
                sendEvent('customer_interaction_viewed', {
                    note_id: note.noteId,
                    customer_id: note.customerId,
                    note_type: note.noteType,
                });
            }
        },
        triggerOnce: true,
    });

    // Combine refs: forwardRef and inViewRef
    const setRefs = (node: HTMLDivElement | null): void => {
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
        inViewRef(node);
    };

    const handleClick = (): void => {
        void DialogStateManager.show(EditNoteDialog, { noteId: note.noteId });
    };

    const isFailedVisit = note.noteType === 'VISIT' && note.interactionHappened === false;
    const isFailedPhoneCall = note.noteType === 'PHONE_CALL' && note.interactionHappened === false;

    // Get formatted time for EVENT and PHONE_CALL types
    const getFormattedTime = (): string | null => {
        if (note.noteType !== 'NOTE') {
            return intlFormat(
                note.occurredAt,
                {
                    hour: 'numeric',
                    minute: 'numeric',
                },
                {
                    locale,
                },
            );
        }
        return null;
    };

    return (
        <TimelineList.Item
            icon={isFailedVisit ? CalendarXIcon : isFailedPhoneCall ? PhoneOffIcon : ClipboardCheckIcon}
            onClick={handleClick}
            ref={setRefs}
            contentClassName={cn({
                'border-orange-100 bg-orange-50 hover:border-[#FEDF89]': !!note.pinnedAt,
            })}
        >
            <TimelineList.ItemHeader
                className={cn({
                    'border-b-0': !note.title,
                    'border-orange-100': !!note.pinnedAt,
                    'bg-orange-50': !!note.pinnedAt,
                })}
            >
                <div className="flex justify-between items-center w-full gap-cozy">
                    <Text>
                        <Text weight="semibold">
                            {isFailedVisit
                                ? t('notes__type__visit_not_reached', { ns: 'notes' })
                                : isFailedPhoneCall
                                  ? t('notes__type__phone_call_not_reached', { ns: 'notes' })
                                  : getNoteTypeLabel(note.noteType, t)}
                        </Text>{' '}
                        {t('logged_by_user', { user: note.createdBy.fullName })}
                    </Text>
                    <div className="flex items-center gap-3 sm:gap-4 shrink-0 whitespace-nowrap">
                        {note.pinnedAt && (
                            <Badge icon={PinIcon} color="yellow">
                                {t('notes__pinned_label', { ns: 'notes' })}
                            </Badge>
                        )}
                        {note.attachments.length > 0 && (
                            <InfoTag icon={PaperclipIcon}>{note.attachments.length}</InfoTag>
                        )}
                        {getFormattedTime() && <InfoTag icon={ClockIcon}>{getFormattedTime()}</InfoTag>}
                    </div>
                </div>
            </TimelineList.ItemHeader>
            {note.title && (
                <TimelineList.ItemBody>
                    <div className="flex flex-row gap-4">
                        <div className="flex-1 overflow-hidden [&_p]:mb-0">
                            {note.pinnedAt ? (
                                <RichTextViewer content={note.titleRich} />
                            ) : (
                                <CollapsibleText>
                                    <RichTextViewer content={note.titleRich} />
                                </CollapsibleText>
                            )}
                        </div>
                    </div>
                </TimelineList.ItemBody>
            )}
        </TimelineList.Item>
    );
});

NoteTimelineItem.displayName = 'NoteTimelineItem';
